import { COMMITTEES_BASE_PATH } from "@/pages/people/membership-services/committees/constants";

export type GlobalSearchDestination = {
  id: string;
  label: string;
  breadcrumbs: string[];
  path: string;
  fallbackPath?: string;
  keywords?: string[];
};

type BuildGlobalSearchDestinationsInput = {
  scormCourses?: boolean;
  activitiesScreen?: boolean;
  achievementsReport?: boolean;
  memberElections?: boolean;
  canViewMcpPlayground?: boolean;
  canViewChatLogs?: boolean;
  canViewM360Configuration?: boolean;
  systemPreferences?: boolean;
  prospectProgramTab?: boolean;
  universityStaff?: boolean;
  canViewPersonMembership?: boolean;
  canViewPersonEducation?: boolean;
  canViewPersonCareer?: boolean;
  canViewCareerPlan?: boolean;
  canViewCareerPreferences?: boolean;
  canViewCompensationProfiles?: boolean;
  canManageJobPosts?: boolean;
  canViewJobPosts?: boolean;
  canViewPersonEvents?: boolean;
  canViewPersonGroups?: boolean;
  canViewPersonChangeHistory?: boolean;
  canViewPersonBilling?: boolean;
  canViewOrders?: boolean;
  canManageApplications?: boolean;
  canViewCommittees?: boolean;
};

export const GLOBAL_SEARCH_RESULTS_PATH = "/search";

export function globalSearchResultsPath(query: string): string {
  const trimmed = query.trim();
  if (!trimmed) return GLOBAL_SEARCH_RESULTS_PATH;
  return `${GLOBAL_SEARCH_RESULTS_PATH}?q=${encodeURIComponent(trimmed)}`;
}

function d(
  label: string,
  breadcrumbs: string[],
  path: string,
  extra?: { fallbackPath?: string; keywords?: string[] }
): GlobalSearchDestination {
  return {
    id: `${breadcrumbs.join(">")}|${label}|${path}`,
    label,
    breadcrumbs,
    path,
    fallbackPath: extra?.fallbackPath,
    keywords: extra?.keywords
  };
}

function isOmittedGlobalSearchPath(path: string, activitiesScreen: boolean): boolean {
  if (path === GLOBAL_SEARCH_RESULTS_PATH) return true;
  if (!activitiesScreen) return false;
  return path === "/events" || (path.startsWith("/events/") && path !== "/events/pat-results");
}

export function buildGlobalSearchDestinations({
  scormCourses = false,
  activitiesScreen = true,
  achievementsReport = true,
  memberElections = true,
  canViewMcpPlayground = false,
  canViewChatLogs = false,
  canViewM360Configuration = false,
  systemPreferences = false,
  prospectProgramTab = false,
  universityStaff = false,
  canViewPersonMembership = true,
  canViewPersonEducation = false,
  canViewPersonCareer = true,
  canViewCareerPlan = true,
  canViewCareerPreferences = true,
  canViewCompensationProfiles = true,
  canManageJobPosts = true,
  canViewJobPosts = true,
  canViewPersonEvents = false,
  canViewPersonGroups = false,
  canViewPersonChangeHistory = false,
  canViewPersonBilling = false,
  canViewOrders = true,
  canManageApplications = true,
  canViewCommittees = true
}: BuildGlobalSearchDestinationsInput = {}): GlobalSearchDestination[] {
  const educationVideosLabel = scormCourses ? "Online Courses" : "Online Videos & Assessments";
  const personFallback = "/people";
  const sectionFallback = "/sections";
  const facilityFallback = "/facilities";
  const eventFallback = "/events";
  const activitiesEventFallback = "/activities/events";
  const jobFallback = "/jobs";
  const universityFallback = "/education/universities";
  const activityTypeFallback = "/activities/activity-types";

  const destinations = [
    d("Home", [], "/home"),
    d("Roster", ["Home"], "/home/roster"),
    d("People", [], "/people"),
    d("Sections", [], "/sections"),
    d("Facilities", [], "/facilities"),
    d("Events", [], "/events"),
    d("Activities", [], "/activities"),
    d("Jobs", [], "/jobs"),
    d("Billing", [], "/billing"),
    d("Education", [], "/education"),
    d("Tasks", [], "/tasks"),
    d("Photobooth", [], "/photobooth"),
    d("System Preferences", [], "/preferences", { keywords: ["dark mode", "appearance", "settings"] }),
    d("Member 360 Configuration", [], "/configuration", {
      keywords: ["settings", "career consultant", "recruiter", "player engagement"]
    }),
    d("Career Consultants", ["Member 360 Configuration"], "/configuration/career-consultants", {
      keywords: ["section coverage", "career services"]
    }),
    d("Player Engagement", ["Member 360 Configuration"], "/configuration/player-engagement"),
    d("Recruiters", ["Member 360 Configuration"], "/configuration/recruiters"),
    d("Chat with Member 360", [], "/dev/chat", { keywords: ["chat", "agent", "mcp", "playground"] }),
    d("Chat Logs", [], "/dev/chat-logs"),

    d("Applications", ["People", "Operations"], "/people/membership-services/applications"),
    d("Achievements", ["People", "Operations"], "/people/membership-services/achievements-report"),
    d("Member Elections", ["People", "Operations"], "/people/membership-services/member-elections"),
    d(
      "Upcoming Member Election Dates",
      ["People", "Operations", "Member Elections"],
      "/people/membership-services/member-elections/upcoming-dates",
      { keywords: ["election calendar"] }
    ),
    d("Committees", ["People", "Operations"], COMMITTEES_BASE_PATH),
    d("Merge/Purge Person Records", ["People", "Operations"], "/people/person-merge-purge", {
      keywords: ["merge purge", "person merge"]
    }),

    d("Overview", ["People", "Person"], "/people/:pgaId/person/overview", { fallbackPath: personFallback }),
    d("Contact", ["People", "Person"], "/people/:pgaId/person/contact", { fallbackPath: personFallback }),
    d("Change Requests", ["People", "Person"], "/people/:pgaId/person/change-requests", {
      fallbackPath: personFallback
    }),
    d("Change History", ["People", "Person"], "/people/:pgaId/person/change-history", {
      fallbackPath: personFallback
    }),
    d("Documents", ["People", "Person"], "/people/:pgaId/person/documents", { fallbackPath: personFallback }),
    d("Groups", ["People", "Person"], "/people/:pgaId/person/groups", { fallbackPath: personFallback }),
    d("Timeline", ["People", "Person"], "/people/:pgaId/person/timeline", { fallbackPath: personFallback }),
    d("Notes", ["People", "Person"], "/people/:pgaId/person/notes", { fallbackPath: personFallback }),
    d("Membership", ["People", "Person"], "/people/:pgaId/membership", { fallbackPath: personFallback }),
    d("Overview", ["People", "Person", "Membership"], "/people/:pgaId/membership/overview", {
      fallbackPath: personFallback
    }),
    d("Path to Membership", ["People", "Person", "Membership"], "/people/:pgaId/membership/path-to-membership", {
      fallbackPath: personFallback
    }),
    d("Prospect Program", ["People", "Person", "Membership"], "/people/:pgaId/membership/prospect-program", {
      fallbackPath: personFallback
    }),
    d("Applications", ["People", "Person", "Membership"], "/people/:pgaId/membership/applications", {
      fallbackPath: personFallback
    }),
    d("Section Affiliation", ["People", "Person", "Membership"], "/people/:pgaId/membership/section", {
      fallbackPath: personFallback
    }),
    d("Committees", ["People", "Person", "Membership"], "/people/:pgaId/membership/committees", {
      fallbackPath: personFallback
    }),
    d("Education", ["People", "Person"], "/people/:pgaId/education", { fallbackPath: personFallback }),
    d(
      "Professional Development (PDRs)",
      ["People", "Person", "Education"],
      "/people/:pgaId/education/professional-development",
      { fallbackPath: personFallback, keywords: ["pdr"] }
    ),
    d("Lifelong Learning", ["People", "Person", "Education"], "/people/:pgaId/education/learning", {
      fallbackPath: personFallback
    }),
    d("PGM Program", ["People", "Person", "Education"], "/people/:pgaId/education/program", {
      fallbackPath: personFallback
    }),
    d("Academic Degrees", ["People", "Person", "Education"], "/people/:pgaId/education/credentials", {
      fallbackPath: personFallback
    }),
    d("Certifications", ["People", "Person", "Education"], "/people/:pgaId/education/certifications", {
      fallbackPath: personFallback
    }),
    d("Career", ["People", "Person"], "/people/:pgaId/career", { fallbackPath: personFallback }),
    d("Overview", ["People", "Person", "Career"], "/people/:pgaId/career/overview", { fallbackPath: personFallback }),
    d("Employment", ["People", "Person", "Career"], "/people/:pgaId/career/employment", {
      fallbackPath: personFallback
    }),
    d("Awards", ["People", "Person", "Career"], "/people/:pgaId/career/awards", { fallbackPath: personFallback }),
    d("Governance Roles", ["People", "Person", "Career"], "/people/:pgaId/career/governance-roles", {
      fallbackPath: personFallback
    }),
    d("Career Profile", ["People", "Person", "Career"], "/people/:pgaId/career/career-profile", {
      fallbackPath: personFallback
    }),
    d("Career Plan", ["People", "Person", "Career"], "/people/:pgaId/career/career-plan", {
      fallbackPath: personFallback
    }),
    d("Applications", ["People", "Person", "Career"], "/people/:pgaId/career/job-applications", {
      fallbackPath: personFallback
    }),
    d("Preferences", ["People", "Person", "Career"], "/people/:pgaId/career/job-preferences", {
      fallbackPath: personFallback
    }),
    d("Compensation", ["People", "Person", "Career"], "/people/:pgaId/career/compensation", {
      fallbackPath: personFallback
    }),
    d("Compensation Profiles", ["People", "Person", "Career"], "/people/:pgaId/career/compensation-profiles", {
      fallbackPath: personFallback
    }),
    d("Job Postings", ["People", "Person", "Career"], "/people/:pgaId/career/job-postings", {
      fallbackPath: personFallback
    }),
    d("Event Registrations", ["People", "Person", "Events"], "/people/:pgaId/events/registrations", {
      fallbackPath: personFallback,
      keywords: ["evr"]
    }),
    d("Cancellations", ["People", "Person", "Events"], "/people/:pgaId/events/cancellations", {
      fallbackPath: personFallback
    }),
    d("Participation History", ["People", "Person", "Events"], "/people/:pgaId/events/participation-history", {
      fallbackPath: personFallback
    }),
    d("Orders", ["People", "Person", "Billing"], "/people/:pgaId/billing/orders", { fallbackPath: personFallback }),
    d("Payout Profiles", ["People", "Person", "Billing"], "/people/:pgaId/billing/payout-profiles", {
      fallbackPath: personFallback
    }),
    d("Benefits", ["People", "Person"], "/people/:pgaId/benefits", { fallbackPath: personFallback }),
    d("Beneficiaries", ["People", "Person", "Benefits"], "/people/:pgaId/benefits/beneficiaries", {
      fallbackPath: personFallback
    }),
    d("Interactions", ["People", "Person"], "/people/:pgaId/interactions", { fallbackPath: personFallback }),
    d("Interactions", ["People", "Person", "Interactions"], "/people/:pgaId/interactions/interactions", {
      fallbackPath: personFallback
    }),
    d("Person History", ["People", "Person", "Interactions"], "/people/:pgaId/interactions/person-history", {
      fallbackPath: personFallback
    }),
    d("Admin Tools", ["People", "Person"], "/people/:pgaId/admin-tools", { fallbackPath: personFallback }),

    d("People in Section", ["Sections", "People"], "/sections/:sectionId/people/section-people", {
      fallbackPath: sectionFallback
    }),
    d("Change Requests", ["Sections", "People"], "/sections/:sectionId/people/change-requests", {
      fallbackPath: sectionFallback
    }),
    d("Roster Activity", ["Sections", "People"], "/sections/:sectionId/roster-activity", {
      fallbackPath: sectionFallback
    }),
    d("Audit Log", ["Sections", "People"], "/sections/:sectionId/audit-log", { fallbackPath: sectionFallback }),
    d("Jobs", ["Sections"], "/sections/:sectionId/jobs/list", { fallbackPath: sectionFallback }),
    d("Create Job", ["Sections", "Jobs"], "/sections/:sectionId/jobs/job-post/create", {
      fallbackPath: sectionFallback
    }),
    d("Applicants", ["Sections", "Jobs", "Job"], "/sections/:sectionId/jobs/job-post/:jobPostId/applicants", {
      fallbackPath: sectionFallback
    }),
    d("Reviews", ["Sections", "Jobs", "Job"], "/sections/:sectionId/jobs/job-post/:jobPostId/reviews", {
      fallbackPath: sectionFallback
    }),
    d("Overview", ["Sections", "Jobs", "Job"], "/sections/:sectionId/jobs/job-post/:jobPostId/details", {
      fallbackPath: sectionFallback
    }),
    d("Interactions", ["Sections", "Jobs", "Job"], "/sections/:sectionId/jobs/job-post/:jobPostId/interactions", {
      fallbackPath: sectionFallback
    }),
    d("Outcome", ["Sections", "Jobs", "Job"], "/sections/:sectionId/jobs/job-post/:jobPostId/outcome", {
      fallbackPath: sectionFallback
    }),
    d("Dues Rates", ["Sections", "Billing"], "/sections/:sectionId/billing/dues-rates", {
      fallbackPath: sectionFallback
    }),
    d("Committees", ["Sections", "Operations"], "/sections/:sectionId/operations/committees", {
      fallbackPath: sectionFallback
    }),
    d("Employees", ["Sections", "Operations"], "/sections/:sectionId/operations/employees", {
      fallbackPath: sectionFallback
    }),
    d("Governance", ["Sections", "Operations"], "/sections/:sectionId/operations/governance", {
      fallbackPath: sectionFallback
    }),
    d("Contact", ["Sections", "Operations"], "/sections/:sectionId/operations/contact", {
      fallbackPath: sectionFallback
    }),
    d("Notifications", ["Sections", "Operations"], "/sections/:sectionId/operations/notifications", {
      fallbackPath: sectionFallback
    }),

    d("Employees", ["Facilities", "People"], "/facilities/:facilityId/people/employees", {
      fallbackPath: facilityFallback
    }),
    d("Former Employees", ["Facilities", "People"], "/facilities/:facilityId/people/former_employees", {
      fallbackPath: facilityFallback
    }),
    d("Governance", ["Facilities", "People"], "/facilities/:facilityId/people/governance", {
      fallbackPath: facilityFallback
    }),
    d("New Facility", ["Facilities"], "/facilities/new"),
    d("Jobs", ["Facilities"], "/facilities/:facilityId/jobs", { fallbackPath: facilityFallback }),
    d("Job Permissions", ["Facilities", "Jobs"], "/facilities/:facilityId/jobs/permissions", {
      fallbackPath: facilityFallback
    }),
    d("Create Job", ["Facilities", "Jobs"], "/facilities/:facilityId/jobs/job-post/create", {
      fallbackPath: facilityFallback
    }),
    d("Applicants", ["Facilities", "Jobs", "Job"], "/facilities/:facilityId/jobs/job-post/:jobPostId/applicants", {
      fallbackPath: facilityFallback
    }),
    d("Reviews", ["Facilities", "Jobs", "Job"], "/facilities/:facilityId/jobs/job-post/:jobPostId/reviews", {
      fallbackPath: facilityFallback
    }),
    d("Overview", ["Facilities", "Jobs", "Job"], "/facilities/:facilityId/jobs/job-post/:jobPostId/details", {
      fallbackPath: facilityFallback
    }),
    d("Interactions", ["Facilities", "Jobs", "Job"], "/facilities/:facilityId/jobs/job-post/:jobPostId/interactions", {
      fallbackPath: facilityFallback
    }),
    d("Outcome", ["Facilities", "Jobs", "Job"], "/facilities/:facilityId/jobs/job-post/:jobPostId/outcome", {
      fallbackPath: facilityFallback
    }),
    d("Contact", ["Facilities"], "/facilities/:facilityId/details/contact", { fallbackPath: facilityFallback }),
    d("Notes", ["Facilities"], "/facilities/:facilityId/details/notes", { fallbackPath: facilityFallback }),
    d("Admin Tools", ["Facilities"], "/facilities/:facilityId/admin-tools", { fallbackPath: facilityFallback }),

    d("New Event", ["Events"], "/events/new"),
    d("PAT Results", ["Events"], "/events/pat-results", { keywords: ["playing ability test"] }),
    d("Overview", ["Events", "Event"], "/events/:eventSlug/overview", { fallbackPath: eventFallback }),
    d("Registrations", ["Events", "Event"], "/events/:eventSlug/registrations", { fallbackPath: eventFallback }),
    d("Waitlist", ["Events", "Event"], "/events/:eventSlug/waitlist", { fallbackPath: eventFallback }),
    d("Cancellations", ["Events", "Event"], "/events/:eventSlug/cancellations", { fallbackPath: eventFallback }),
    d("Attendance", ["Events", "Event"], "/events/:eventSlug/attendance", { fallbackPath: eventFallback }),
    d("Leads", ["Events", "Event"], "/events/:eventSlug/leads", { fallbackPath: eventFallback }),
    d("Results", ["Events", "Event"], "/events/:eventSlug/results", { fallbackPath: eventFallback }),
    d("Payouts", ["Events", "Event"], "/events/:eventSlug/payouts", { fallbackPath: eventFallback }),
    d("Notes and Tasks", ["Events", "Event"], "/events/:eventSlug/notes", { fallbackPath: eventFallback }),
    d("Recap", ["Events", "Event"], "/events/:eventSlug/recap", { fallbackPath: eventFallback }),

    d("Events", ["Activities"], "/activities/events"),
    d("New Event", ["Activities", "Events"], "/activities/events/new"),
    d("PDR Activity Types", ["Activities"], "/activities/activity-types", { keywords: ["pdr"] }),
    d("Activities", ["Activities", "PDR Activity Types"], "/activities/activity-types/:activityTypeId/activities", {
      fallbackPath: activityTypeFallback
    }),
    d(
      "Activity Participants",
      ["Activities", "PDR Activity Types"],
      "/activities/activity-types/:activityTypeId/participants",
      { fallbackPath: activityTypeFallback }
    ),
    d(
      "Activity Participants",
      ["Activities", "PDR Activity Types", "Activity"],
      "/activities/activity-types/:activityTypeId/activity/:activityId/participants",
      { fallbackPath: activityTypeFallback }
    ),
    d("Overview", ["Activities", "Event"], "/activities/events/:eventSlug/overview", {
      fallbackPath: activitiesEventFallback
    }),
    d("Registrations", ["Activities", "Event"], "/activities/events/:eventSlug/registrations", {
      fallbackPath: activitiesEventFallback
    }),
    d("Waitlist", ["Activities", "Event"], "/activities/events/:eventSlug/waitlist", {
      fallbackPath: activitiesEventFallback
    }),
    d("Cancellations", ["Activities", "Event"], "/activities/events/:eventSlug/cancellations", {
      fallbackPath: activitiesEventFallback
    }),
    d("Attendance", ["Activities", "Event"], "/activities/events/:eventSlug/attendance", {
      fallbackPath: activitiesEventFallback
    }),
    d("Leads", ["Activities", "Event"], "/activities/events/:eventSlug/leads", {
      fallbackPath: activitiesEventFallback
    }),
    d("Results", ["Activities", "Event"], "/activities/events/:eventSlug/results", {
      fallbackPath: activitiesEventFallback
    }),
    d("Payouts", ["Activities", "Event"], "/activities/events/:eventSlug/payouts", {
      fallbackPath: activitiesEventFallback
    }),
    d("Notes and Tasks", ["Activities", "Event"], "/activities/events/:eventSlug/notes", {
      fallbackPath: activitiesEventFallback
    }),
    d("Recap", ["Activities", "Event"], "/activities/events/:eventSlug/recap", {
      fallbackPath: activitiesEventFallback
    }),

    d("My Jobs", ["Jobs"], "/jobs/my-jobs"),
    d("Jobs", ["Jobs"], "/jobs/all-jobs"),
    d("ExecuSearch", ["Jobs"], "/jobs/execusearch"),
    d("Create Job", ["Jobs"], "/jobs/job-post/create"),
    d("Applicants", ["Jobs", "Job"], "/jobs/job-post/:jobPostId/applicants", { fallbackPath: jobFallback }),
    d("Reviews", ["Jobs", "Job"], "/jobs/job-post/:jobPostId/reviews", { fallbackPath: jobFallback }),
    d("Overview", ["Jobs", "Job"], "/jobs/job-post/:jobPostId/details", { fallbackPath: jobFallback }),
    d("Interactions", ["Jobs", "Job"], "/jobs/job-post/:jobPostId/interactions", { fallbackPath: jobFallback }),
    d("Outcome", ["Jobs", "Job"], "/jobs/job-post/:jobPostId/outcome", { fallbackPath: jobFallback }),

    d("Orders", ["Billing"], "/billing/orders"),
    d("Apply Payment", ["Billing", "Orders"], "/billing/orders/apply-payment"),
    d("Payments", ["Billing"], "/billing/payments"),
    d("Payout Profiles", ["Billing"], "/billing/payout-profiles"),
    d("Dues Management", ["Billing"], "/billing/dues-management"),
    d("Products", ["Billing", "Dues Management"], "/billing/dues-management/manage-products"),
    d(
      "National Annual Dues and Fees",
      ["Billing", "Dues Management", "Products"],
      "/billing/dues-management/manage-products/annual-national-rates"
    ),
    d(
      "Sections Annual Dues and Fees",
      ["Billing", "Dues Management", "Products"],
      "/billing/dues-management/manage-products/annual-section-rates"
    ),
    d(
      "Eligible for Suspension",
      ["Billing", "Dues Management"],
      "/billing/dues-management/suspensions-and-terminations/suspensions"
    ),
    d(
      "Eligible for Termination",
      ["Billing", "Dues Management"],
      "/billing/dues-management/suspensions-and-terminations/terminations"
    ),
    d("Reporting", ["Billing", "Dues Management"], "/billing/dues-management/reporting"),
    d("Unpaid Dues", ["Billing", "Dues Management", "Reporting"], "/billing/dues-management/reporting/unpaid-dues"),
    d("Suspensions", ["Billing", "Dues Management", "Reporting"], "/billing/dues-management/reporting/suspensions"),
    d("Terminations", ["Billing", "Dues Management", "Reporting"], "/billing/dues-management/reporting/terminations"),
    d("Finance", ["Billing"], "/billing/finance"),
    d("Golf Retirement Plus", ["Billing", "Finance"], "/billing/finance/grp", { keywords: ["grp"] }),
    d("Enrollments", ["Billing", "Finance", "Golf Retirement Plus"], "/billing/finance/grp/enrollments", {
      keywords: ["grp"]
    }),
    d(
      "Member Contributions",
      ["Billing", "Finance", "Golf Retirement Plus"],
      "/billing/finance/grp/member-contributions",
      { keywords: ["grp"] }
    ),
    d(
      "Sponsor Contributions",
      ["Billing", "Finance", "Golf Retirement Plus"],
      "/billing/finance/grp/sponsor-contributions",
      { keywords: ["grp"] }
    ),

    d(educationVideosLabel, ["Education"], "/education/videos", {
      keywords: ["online videos", "assessments", "courses", "scorm", "videos"]
    }),
    d("Publish Video", ["Education", educationVideosLabel], "/education/videos/publish-video"),
    d("PGM Universities", ["Education"], "/education/universities"),
    d("Students", ["Education", "PGM Universities"], "/education/universities/:universityId/students", {
      fallbackPath: universityFallback
    }),
    d(
      "Current Students",
      ["Education", "PGM Universities", "Students"],
      "/education/universities/:universityId/students/current",
      { fallbackPath: universityFallback }
    ),
    d(
      "Eligible for Enrollment",
      ["Education", "PGM Universities", "Students"],
      "/education/universities/:universityId/students/eligible-for-enrollment",
      { fallbackPath: universityFallback }
    ),
    d(
      "Probations",
      ["Education", "PGM Universities", "Students"],
      "/education/universities/:universityId/students/probations",
      { fallbackPath: universityFallback }
    ),
    d(
      "Eligible for Graduation",
      ["Education", "PGM Universities", "Students"],
      "/education/universities/:universityId/students/graduation-eligible",
      { fallbackPath: universityFallback }
    ),
    d(
      "Alumni",
      ["Education", "PGM Universities", "Students"],
      "/education/universities/:universityId/students/alumni",
      { fallbackPath: universityFallback }
    ),
    d(
      "Upload Roster",
      ["Education", "PGM Universities", "Students"],
      "/education/universities/:universityId/students/upload-roster",
      { fallbackPath: universityFallback }
    ),
    d("Events", ["Education", "PGM Universities"], "/education/universities/:universityId/events", {
      fallbackPath: universityFallback
    }),
    d("New Event", ["Education", "PGM Universities", "Events"], "/education/universities/:universityId/events/new", {
      fallbackPath: universityFallback
    }),
    d(
      "Overview",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/overview",
      { fallbackPath: universityFallback }
    ),
    d(
      "Registrations",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/registrations",
      { fallbackPath: universityFallback }
    ),
    d(
      "Waitlist",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/waitlist",
      { fallbackPath: universityFallback }
    ),
    d(
      "Cancellations",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/cancellations",
      { fallbackPath: universityFallback }
    ),
    d(
      "Attendance",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/attendance",
      { fallbackPath: universityFallback }
    ),
    d(
      "Leads",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/leads",
      { fallbackPath: universityFallback }
    ),
    d(
      "Results",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/results",
      { fallbackPath: universityFallback }
    ),
    d(
      "Payouts",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/payouts",
      { fallbackPath: universityFallback }
    ),
    d(
      "Notes and Tasks",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/notes",
      { fallbackPath: universityFallback }
    ),
    d(
      "Recap",
      ["Education", "PGM Universities", "Events", "Event"],
      "/education/universities/:universityId/events/:eventSlug/recap",
      { fallbackPath: universityFallback }
    ),
    d("Internships", ["Education", "PGM Universities"], "/education/universities/:universityId/internships", {
      fallbackPath: universityFallback
    }),
    d(
      "Current Students Internships",
      ["Education", "PGM Universities", "Internships"],
      "/education/universities/:universityId/internships/current",
      { fallbackPath: universityFallback }
    ),
    d(
      "Alumni Internships",
      ["Education", "PGM Universities", "Internships"],
      "/education/universities/:universityId/internships/alumni",
      { fallbackPath: universityFallback }
    ),
    d("Test Results", ["Education", "PGM Universities"], "/education/universities/:universityId/test-results", {
      fallbackPath: universityFallback
    }),
    d(
      "Current Students Test Results",
      ["Education", "PGM Universities", "Test Results"],
      "/education/universities/:universityId/test-results/current",
      { fallbackPath: universityFallback }
    ),
    d(
      "Alumni Test Results",
      ["Education", "PGM Universities", "Test Results"],
      "/education/universities/:universityId/test-results/alumni",
      { fallbackPath: universityFallback }
    ),
    d(
      "PAT Results",
      ["Education", "PGM Universities", "Test Results"],
      "/education/universities/:universityId/test-results/pat-statuses",
      { fallbackPath: universityFallback, keywords: ["playing ability test"] }
    ),
    d("Reports", ["Education", "PGM Universities"], "/education/universities/:universityId/reports", {
      fallbackPath: universityFallback
    }),
    d(
      "Semester",
      ["Education", "PGM Universities", "Reports"],
      "/education/universities/:universityId/reports/semester",
      { fallbackPath: universityFallback }
    ),
    d(
      "Internship Reporting",
      ["Education", "PGM Universities", "Reports"],
      "/education/universities/:universityId/reports/internship-reporting",
      { fallbackPath: universityFallback }
    ),
    d(
      "Past Roster Uploads",
      ["Education", "PGM Universities", "Reports"],
      "/education/universities/:universityId/reports/past-roster-uploads",
      { fallbackPath: universityFallback }
    ),
    d("Staff", ["Education", "PGM Universities"], "/education/universities/:universityId/staff", {
      fallbackPath: universityFallback
    }),

    d("My Tasks", ["Tasks"], "/tasks/my-tasks"),
    d("Department Tasks", ["Tasks"], "/tasks/department-tasks")
  ];

  const showMembershipNav = canViewPersonMembership || canManageApplications || canViewCommittees;
  const showCareerNav =
    canViewPersonCareer ||
    canViewCareerPlan ||
    canViewCareerPreferences ||
    canViewCompensationProfiles ||
    canManageJobPosts ||
    canViewJobPosts;
  const careerDataPaths = new Set([
    "/people/:pgaId/career/employment",
    "/people/:pgaId/career/awards",
    "/people/:pgaId/career/governance-roles"
  ]);
  const showPersonBillingNav = canViewOrders || canViewPersonBilling;
  const membershipDataPaths = new Set([
    "/people/:pgaId/membership/overview",
    "/people/:pgaId/membership/path-to-membership",
    "/people/:pgaId/membership/prospect-program"
  ]);

  return destinations.filter((destination) => {
    if (isOmittedGlobalSearchPath(destination.path, activitiesScreen)) return false;
    if (!showMembershipNav && destination.path.startsWith("/people/:pgaId/membership")) {
      return false;
    }
    if (!canViewPersonMembership && membershipDataPaths.has(destination.path)) {
      return false;
    }
    if (!canManageApplications && destination.path === "/people/:pgaId/membership/applications") {
      return false;
    }
    if (!canViewPersonEducation && destination.path.startsWith("/people/:pgaId/education")) {
      return false;
    }
    if (!showCareerNav && destination.path.startsWith("/people/:pgaId/career")) {
      return false;
    }
    if (!canViewPersonCareer && careerDataPaths.has(destination.path)) {
      return false;
    }
    if (!canViewCareerPlan && destination.path.startsWith("/people/:pgaId/career/career-profile")) {
      return false;
    }
    if (!canViewCareerPlan && destination.path.startsWith("/people/:pgaId/career/career-plan")) {
      return false;
    }
    if (!canManageJobPosts && destination.path === "/people/:pgaId/career/job-applications") {
      return false;
    }
    if (!canViewCareerPreferences && destination.path === "/people/:pgaId/career/job-preferences") {
      return false;
    }
    if (!canViewCompensationProfiles && destination.path === "/people/:pgaId/career/compensation") {
      return false;
    }
    if (!canViewCompensationProfiles && destination.path === "/people/:pgaId/career/compensation-profiles") {
      return false;
    }
    if (!canViewJobPosts && destination.path === "/people/:pgaId/career/job-postings") {
      return false;
    }
    if (
      !canViewPersonCareer &&
      !canViewCareerPreferences &&
      !canViewCompensationProfiles &&
      !canManageJobPosts &&
      destination.path === "/people/:pgaId/career/overview"
    ) {
      return false;
    }
    if (!canViewPersonEvents && destination.path.startsWith("/people/:pgaId/events")) {
      return false;
    }
    if (!canViewPersonGroups && destination.path === "/people/:pgaId/person/groups") {
      return false;
    }
    if (
      !canViewPersonChangeHistory &&
      (destination.path === "/people/:pgaId/person/change-requests" ||
        destination.path === "/people/:pgaId/person/change-history")
    ) {
      return false;
    }
    if (!showPersonBillingNav && destination.path.startsWith("/people/:pgaId/billing")) {
      return false;
    }
    if (!canViewOrders && destination.path === "/people/:pgaId/billing/orders") {
      return false;
    }
    if (!canViewPersonBilling && destination.path === "/people/:pgaId/billing/payout-profiles") {
      return false;
    }
    if (!canViewPersonBilling && destination.path === "/billing/payout-profiles") {
      return false;
    }

    if (universityStaff) return destination.path.startsWith("/education/universities");
    if (!activitiesScreen && (destination.path === "/activities" || destination.path.startsWith("/activities/"))) {
      return false;
    }
    if (!achievementsReport && destination.path === "/people/membership-services/achievements-report") {
      return false;
    }
    if (!memberElections && destination.path.startsWith("/people/membership-services/member-elections")) {
      return false;
    }
    if (!canViewMcpPlayground && destination.path === "/dev/chat") return false;
    if (!canViewChatLogs && destination.path === "/dev/chat-logs") return false;
    if (!canViewM360Configuration && destination.path.startsWith("/configuration")) return false;
    if (!systemPreferences && destination.path === "/preferences") return false;
    if (!prospectProgramTab && destination.path === "/people/:pgaId/membership/prospect-program") return false;
    return true;
  });
}
